import React, { useReducer, useEffect, useMemo, useCallback, useRef } from 'react';
import dayjs from 'dayjs';
import fetchWithAuth, { gpsApiBase } from '../../utils/fetchWithAuth';
import { useGPS } from '../../contexts/GPSContext';
import { FORM_AI_TEASER } from '../../config';
import ErrorBoundary from '../ui/ErrorBoundary';
import GPSFormHeroKPIs from './GPSFormHeroKPIs';
import GPSFormExplorer from './GPSFormExplorer';
import GPSFormAITeaser from './GPSFormAITeaser';
import GPSFormDetailTable from './GPSFormDetailTable';
import GPSFormDetailModal from './GPSFormDetailModal';
import { safeArray, safeNum } from '../../utils/gpsFormUtils';
import { FETCH_DEBOUNCE_MS } from '../../constants/gpsForms';

const ZERO_COVERAGE_COPY =
  'Aucun formulaire n\'est rattaché aux tickets de ce projet. La couverture formulaire n\'est pas encore disponible ici.';

const PERIOD_EMPTY_COPY =
  'Ce projet a des formulaires, mais aucun n\'a été rempli sur la période et les filtres sélectionnés. Élargissez la période ou ajustez les filtres.';

const TAB_ERROR_COPY = 'Une erreur est survenue dans cet onglet.';
const SECTION_ERROR_COPY = 'Une erreur est survenue dans cette section.';

// Soft, non-throwing fallbacks for the two-layer ErrorBoundary net.
const TabFallback = () => (
  <div className="bg-white dark:bg-[#1e1e1e] p-8 rounded-xl border border-gray-200 dark:border-gray-900 text-center">
    <p className="text-gray-600 dark:text-gray-400">{TAB_ERROR_COPY}</p>
  </div>
);

const SectionFallback = () => (
  <div className="bg-white dark:bg-[#1e1e1e] p-6 rounded-xl border border-gray-200 dark:border-gray-900 text-center">
    <p className="text-sm text-gray-500 dark:text-gray-400">{SECTION_ERROR_COPY}</p>
  </div>
);

// §14 skeleton shimmer placeholders for the loading state.
const FormsTabSkeleton = () => (
  <div className="space-y-6 animate-pulse" data-testid="forms-tab-skeleton">
    <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
      {[0, 1, 2].map((i) => (
        <div key={i} className="h-28 bg-gray-200 dark:bg-gray-800 rounded-xl" />
      ))}
    </div>
    <div className="bg-white dark:bg-[#1e1e1e] p-6 rounded-xl border border-gray-200 dark:border-gray-900">
      <div className="h-4 w-1/4 bg-gray-200 dark:bg-gray-800 rounded mb-4" />
      <div className="h-56 bg-gray-200 dark:bg-gray-800 rounded" />
    </div>
    <div className="bg-white dark:bg-[#1e1e1e] p-6 rounded-xl border border-gray-200 dark:border-gray-900 space-y-2">
      <div className="h-8 bg-gray-200 dark:bg-gray-800 rounded" />
      <div className="h-8 bg-gray-200 dark:bg-gray-800 rounded" />
      <div className="h-8 bg-gray-200 dark:bg-gray-800 rounded" />
    </div>
  </div>
);

// ---- single reducer: data + UI selection state ----------------------------

const initialState = {
  // data
  summary: null,
  questions: null,
  answers: null,
  breakdown: null,
  // loading / error flags
  summaryLoading: false,
  questionsLoading: false,
  answersLoading: false,
  breakdownLoading: false,
  summaryError: false,
  questionsError: false,
  answersError: false,
  breakdownError: false,
  // UI selection
  selectedTemplateId: null,
  selectedQuestionId: null,
  // Template owning the selected question — question_id is unique only within a
  // template, so the breakdown/answers fetch must scope on it.
  selectedFormTemplateId: null,
  comparePrevious: false,
  multichoiceDenominator: 'respondents',
  // Detail modal: the clicked form-grain row (header meta) + its lazily-fetched Q/A rows.
  selectedForm: null,
  formDetail: null,
  formDetailLoading: false,
  formDetailError: false,
};

const reducer = (state, action) => {
  switch (action.type) {
    case 'PRIMARY_START':
      // Clear stale data alongside flipping loading so a previous project's
      // summary/questions/answers can never render during the loading gap.
      return {
        ...state,
        summary: null,
        questions: null,
        answers: null,
        breakdown: null,
        summaryLoading: true,
        questionsLoading: true,
        answersLoading: true,
        summaryError: false,
        questionsError: false,
        answersError: false,
        // A filter/project switch invalidates any open form detail.
        selectedForm: null,
        formDetail: null,
        formDetailLoading: false,
        formDetailError: false,
      };
    case 'SUMMARY_OK':
      return { ...state, summary: action.payload, summaryLoading: false, summaryError: false };
    case 'SUMMARY_ERR':
      return { ...state, summary: null, summaryLoading: false, summaryError: true };
    case 'QUESTIONS_OK':
      return { ...state, questions: action.payload, questionsLoading: false, questionsError: false };
    case 'QUESTIONS_ERR':
      return { ...state, questions: null, questionsLoading: false, questionsError: true };
    case 'ANSWERS_OK':
      return { ...state, answers: action.payload, answersLoading: false, answersError: false };
    case 'ANSWERS_ERR':
      return { ...state, answers: null, answersLoading: false, answersError: true };
    case 'BREAKDOWN_START':
      return { ...state, breakdownLoading: true, breakdownError: false };
    case 'BREAKDOWN_OK':
      return { ...state, breakdown: action.payload, breakdownLoading: false, breakdownError: false };
    case 'BREAKDOWN_ERR':
      return { ...state, breakdown: null, breakdownLoading: false, breakdownError: true };
    case 'SELECT_TEMPLATE':
      return {
        ...state,
        selectedTemplateId: action.payload,
        selectedQuestionId: null,
        selectedFormTemplateId: null,
        breakdown: null,
      };
    case 'SELECT_QUESTION':
      // Clear the stale breakdown immediately so the teaser/explorer only react to
      // the new question once its breakdown resolves (no flash of the previous
      // question's type/respondents). Mark loading so the gate stays closed until
      // the breakdown effect confirms the current answerType.
      return {
        ...state,
        selectedQuestionId: action.payload?.questionId ?? null,
        selectedFormTemplateId: action.payload?.formTemplateId ?? null,
        breakdown: null,
        // Only enter loading when BOTH are present — the breakdown fetch is scoped by
        // template, so a question without a template id never fires (no stuck spinner).
        breakdownLoading: !!(action.payload?.questionId && action.payload?.formTemplateId),
        breakdownError: false,
      };
    case 'CLEAR_QUESTION':
      return { ...state, selectedQuestionId: null, selectedFormTemplateId: null, breakdown: null };
    case 'OPEN_FORM':
      return { ...state, selectedForm: action.payload, formDetail: null, formDetailLoading: true, formDetailError: false };
    case 'FORM_DETAIL_OK':
      return { ...state, formDetail: action.payload, formDetailLoading: false, formDetailError: false };
    case 'FORM_DETAIL_ERR':
      return { ...state, formDetail: null, formDetailLoading: false, formDetailError: true };
    case 'CLOSE_FORM':
      return { ...state, selectedForm: null, formDetail: null, formDetailLoading: false, formDetailError: false };
    case 'RESET':
      return { ...initialState };
    default:
      return state;
  }
};

// ---- query-string builder (filters → repeated params) ----------------------

const fmt = (date) => (date ? dayjs(date).format('YYYY-MM-DD') : '');

// Single source of truth for the seven filter arrays consumed by the form
// endpoints. Both the query string and the refetch signature derive from this so
// they can never drift apart. Maps each option to its `value`.
const collectFilterValues = (gps) => ({
  shopsign: safeArray(gps?.selectedShopSigns).map((o) => o?.value),
  sector: safeArray(gps?.selectedSectors).map((o) => o?.value),
  brand: safeArray(gps?.selectedBrands).map((o) => o?.value),
  tiering: safeArray(gps?.selectedTierings).map((o) => o?.value),
  status: safeArray(gps?.selectedStatus).map((o) => o?.value),
  requestType: safeArray(gps?.selectedRequestTypes).map((o) => o?.value),
  interventionPoint: safeArray(gps?.selectedShops).map((o) => o?.value),
});

const buildQueryString = (gps, extra = {}) => {
  const params = new URLSearchParams();
  const start = fmt(gps?.startDate);
  const end = fmt(gps?.endDate);
  if (start) params.set('startDate', start);
  if (end) params.set('endDate', end);

  Object.entries(collectFilterValues(gps)).forEach(([key, values]) => {
    values.forEach((v) => {
      if (v !== undefined && v !== null && v !== '') params.append(key, String(v));
    });
  });

  Object.entries(extra).forEach(([k, v]) => {
    if (v !== undefined && v !== null && v !== '') params.set(k, String(v));
  });

  return params.toString();
};

// ---- container -------------------------------------------------------------

const GPSFormsTab = ({ currentUser }) => {
  const gps = useGPS();
  const [state, dispatch] = useReducer(reducer, initialState);

  const projectKey = gps?.selectedProjet?.value ?? null;
  // Human-readable project label (NOT the projectKey) for analytics props.
  const projectLabel = gps?.selectedProjet?.label ?? null;

  // A stable signature of the period + every consumed filter so effects refetch
  // exactly when the request would change (and not on unrelated context churn).
  const filterSig = useMemo(
    () =>
      JSON.stringify({
        start: fmt(gps?.startDate),
        end: fmt(gps?.endDate),
        ...collectFilterValues(gps),
      }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      gps?.startDate,
      gps?.endDate,
      gps?.selectedShopSigns,
      gps?.selectedSectors,
      gps?.selectedBrands,
      gps?.selectedTierings,
      gps?.selectedStatus,
      gps?.selectedRequestTypes,
      gps?.selectedShops,
    ]
  );

  // Latest-request guard so a stale fetch never clobbers fresh state.
  const reqIdRef = useRef(0);
  // Independent guard for the on-demand form-detail (modal) fetch.
  const formReqRef = useRef(0);

  // Primary fetch: summary + questions + answers (debounced ~300ms).
  useEffect(() => {
    if (!projectKey) {
      dispatch({ type: 'RESET' });
      return;
    }

    const myReq = ++reqIdRef.current;
    // Flip loading (and drop the previous project's data) synchronously, before
    // the debounce, so the skeleton shows immediately on a project/filter switch
    // and no stale/empty full view can flash during the ~300ms gap.
    dispatch({ type: 'PRIMARY_START' });
    const handle = setTimeout(() => {
      const qs = buildQueryString(gps);

      fetchWithAuth(`${gpsApiBase(projectKey)}/form-summary?${qs}`)
        .then((d) => { if (myReq === reqIdRef.current) dispatch({ type: 'SUMMARY_OK', payload: d }); })
        .catch(() => { if (myReq === reqIdRef.current) dispatch({ type: 'SUMMARY_ERR' }); });

      fetchWithAuth(`${gpsApiBase(projectKey)}/form-questions?${qs}`)
        .then((d) => { if (myReq === reqIdRef.current) dispatch({ type: 'QUESTIONS_OK', payload: d }); })
        .catch(() => { if (myReq === reqIdRef.current) dispatch({ type: 'QUESTIONS_ERR' }); });

      // Detail table is form-grain (one row per filled form): groupBy=form.
      fetchWithAuth(`${gpsApiBase(projectKey)}/form-answers?${buildQueryString(gps, { groupBy: 'form' })}`)
        .then((d) => { if (myReq === reqIdRef.current) dispatch({ type: 'ANSWERS_OK', payload: d }); })
        .catch(() => { if (myReq === reqIdRef.current) dispatch({ type: 'ANSWERS_ERR' }); });
    }, FETCH_DEBOUNCE_MS);

    return () => clearTimeout(handle);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectKey, filterSig]);

  // Question-scoped fetch: breakdown + answers pre-filtered by the question.
  // question_id is unique only within a template, so we require BOTH the question and
  // its owning template id before firing — never an unscoped question fetch.
  useEffect(() => {
    if (!projectKey || !state.selectedQuestionId || !state.selectedFormTemplateId) return;

    const myReq = ++reqIdRef.current;
    dispatch({ type: 'BREAKDOWN_START' });
    const qid = state.selectedQuestionId;
    const templateExtra = { formTemplateId: state.selectedFormTemplateId };
    const qs = buildQueryString(gps, templateExtra);
    // Form-grain answers restricted to the forms that answered this question.
    const answersQs = buildQueryString(gps, { ...templateExtra, questionId: qid, groupBy: 'form' });

    fetchWithAuth(`${gpsApiBase(projectKey)}/form-questions/${encodeURIComponent(qid)}/breakdown?${qs}`)
      .then((d) => { if (myReq === reqIdRef.current) dispatch({ type: 'BREAKDOWN_OK', payload: d }); })
      .catch(() => { if (myReq === reqIdRef.current) dispatch({ type: 'BREAKDOWN_ERR' }); });

    fetchWithAuth(`${gpsApiBase(projectKey)}/form-answers?${answersQs}`)
      .then((d) => { if (myReq === reqIdRef.current) dispatch({ type: 'ANSWERS_OK', payload: d }); })
      .catch(() => { if (myReq === reqIdRef.current) dispatch({ type: 'ANSWERS_ERR' }); });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectKey, state.selectedQuestionId, state.selectedFormTemplateId, filterSig]);

  // On-demand form-detail fetch (modal): flat Q/A rows for the clicked form.
  const selectedFormId = state.selectedForm?.formId ?? null;
  const hasOpenForm = !!state.selectedForm;
  useEffect(() => {
    if (!projectKey || !hasOpenForm) return;

    // A form-grain row without a formId can't be drilled into — resolve to a safe
    // empty state rather than leaving the modal stuck on its loading skeleton.
    if (!selectedFormId) {
      dispatch({ type: 'FORM_DETAIL_OK', payload: { rows: [] } });
      return;
    }

    const myReq = ++formReqRef.current;
    const qs = buildQueryString(gps, { formId: selectedFormId });
    fetchWithAuth(`${gpsApiBase(projectKey)}/form-answers?${qs}`)
      .then((d) => { if (myReq === formReqRef.current) dispatch({ type: 'FORM_DETAIL_OK', payload: d }); })
      .catch(() => { if (myReq === formReqRef.current) dispatch({ type: 'FORM_DETAIL_ERR' }); });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectKey, hasOpenForm, selectedFormId, filterSig]);

  // ---- derived ----
  const selectedQuestionLabel = useMemo(() => {
    if (!state.selectedQuestionId) return null;
    const templates = safeArray(state.questions?.templates);
    // Scope to the owning template when known — question_id is unique only within a
    // template, so an unscoped lookup could return a same-numbered question's label.
    const scoped = state.selectedFormTemplateId
      ? templates.filter((t) => t?.templateId === state.selectedFormTemplateId)
      : templates;
    for (const t of scoped) {
      const q = safeArray(t?.questions).find((x) => x?.questionId === state.selectedQuestionId);
      if (q) return q.label || q.questionId;
    }
    return null;
  }, [state.selectedQuestionId, state.selectedFormTemplateId, state.questions]);

  // Forms sync timestamp (distinct from the issues sync shown in the GPS shell).
  const formsSyncedAt = useMemo(() => {
    const ts =
      state.summary?.metadata?.formsSyncedAt ||
      state.questions?.metadata?.formsSyncedAt ||
      state.answers?.metadata?.formsSyncedAt ||
      null;
    if (!ts) return null;
    const d = dayjs(ts);
    return d.isValid() ? d.format('DD/MM/YYYY HH:mm') : null;
  }, [state.summary, state.questions, state.answers]);

  // Section C teaser only for a text question (reuses the fetched breakdown).
  // Gate on !breakdownLoading so a question switch never flashes the previous
  // question's teaser/count before the current breakdown confirms answerType.
  const isTextQuestion = state.breakdown?.answerType === 'text';
  const textRespondents = safeNum(state.breakdown?.current?.respondents);
  const showTeaser = FORM_AI_TEASER && !state.breakdownLoading && isTextQuestion;

  // Per-section ErrorBoundary reset keys: the refetch signature plus the resolved
  // data object for that section. When an in-tab refetch (project/period/filter,
  // or question for the question-scoped sections) resolves to valid data, the
  // changed key clears any latched render error — so a section stuck on its
  // fallback recovers automatically, without waiting for a tab switch.
  const refetchSig = `${projectKey}|${filterSig}`;
  const questionSig = `${refetchSig}|${state.selectedQuestionId ?? ''}`;

  // ---- handlers ----
  const handleSelectTemplate = useCallback((id) => dispatch({ type: 'SELECT_TEMPLATE', payload: id }), []);
  const handleSelectQuestion = useCallback(
    (id, formTemplateId = null) =>
      dispatch({ type: 'SELECT_QUESTION', payload: { questionId: id, formTemplateId } }),
    []
  );
  const handleClearQuestion = useCallback(() => dispatch({ type: 'CLEAR_QUESTION' }), []);
  const handleOpenForm = useCallback((row) => dispatch({ type: 'OPEN_FORM', payload: row }), []);
  const handleCloseForm = useCallback(() => dispatch({ type: 'CLOSE_FORM' }), []);

  // ---- global states ----

  if (!projectKey) {
    return (
      <div className="bg-white dark:bg-[#1e1e1e] p-8 rounded-xl border border-gray-200 dark:border-gray-900 text-center">
        <p className="text-gray-600 dark:text-gray-400">Sélectionnez un projet pour visualiser les formulaires.</p>
      </div>
    );
  }

  const primaryLoading = state.summaryLoading || state.questionsLoading;

  if (primaryLoading && !state.summary) {
    return <FormsTabSkeleton />;
  }

  // Soft error state: summary failed and we have nothing to show.
  if (state.summaryError && !state.summary) {
    return (
      <div className="bg-white dark:bg-[#1e1e1e] p-8 rounded-xl border border-gray-200 dark:border-gray-900 text-center">
        <p className="text-gray-600 dark:text-gray-400">Impossible de charger les formulaires pour le moment. Réessayez plus tard.</p>
      </div>
    );
  }

  const formsCollected = safeNum(state.summary?.current?.formsCollected);

  // No-forms-for-project → zero-coverage empty state (NO tiles/explorer/table, never "0 %").
  // Gated on a resolved (non-loading) summary so it only appears after an actually
  // empty result — never during the project-switch loading gap.
  if (!primaryLoading && state.summary && formsCollected <= 0) {
    // Distinguish "project has no forms at all" from "none on this period/filters"
    // (projectHasForms ignores the period + filters, computed server-side).
    const projectHasForms = !!state.summary?.metadata?.projectHasForms;
    const emptyHeading = projectHasForms ? 'Aucun formulaire sur cette période' : 'Aucune donnée formulaire pour ce projet';
    const emptyCopy = projectHasForms ? PERIOD_EMPTY_COPY : ZERO_COVERAGE_COPY;
    return (
      <div className="bg-white dark:bg-[#1e1e1e] p-10 rounded-xl border border-gray-200 dark:border-gray-900 text-center">
        <svg xmlns="http://www.w3.org/2000/svg" className="h-12 w-12 mx-auto text-gray-300 dark:text-gray-600 mb-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
        </svg>
        <h3 className="text-base font-semibold text-gray-900 dark:text-white mb-2">{emptyHeading}</h3>
        <p className="text-sm text-gray-600 dark:text-gray-400 max-w-md mx-auto">{emptyCopy}</p>
      </div>
    );
  }

  // Global boundary wraps the whole tab; each section gets its own so one failing
  // section degrades locally while its siblings keep rendering.
  return (
    <ErrorBoundary fallback={<TabFallback />}>
      <div className="space-y-6">
        {formsSyncedAt && (
          <div className="flex justify-end -mb-2">
            <span className="text-xs text-gray-500 dark:text-gray-400">
              Dernière synchronisation des formulaires : {formsSyncedAt}
            </span>
          </div>
        )}

        <ErrorBoundary resetKeys={[refetchSig, state.summary]} fallback={<SectionFallback />}>
          <GPSFormHeroKPIs summary={state.summary} />
        </ErrorBoundary>

        <ErrorBoundary resetKeys={[questionSig, state.questions, state.breakdown]} fallback={<SectionFallback />}>
          <GPSFormExplorer
            questions={state.questions}
            breakdown={state.breakdown}
            breakdownLoading={state.breakdownLoading}
            breakdownError={state.breakdownError}
            selectedTemplateId={state.selectedTemplateId}
            selectedQuestionId={state.selectedQuestionId}
            comparePrevious={state.comparePrevious}
            denominator={state.multichoiceDenominator}
            onSelectTemplate={handleSelectTemplate}
            onSelectQuestion={handleSelectQuestion}
            project={projectLabel}
            currentUser={currentUser}
          />
        </ErrorBoundary>

        {showTeaser && (
          <ErrorBoundary resetKeys={[questionSig, state.breakdown]} fallback={<SectionFallback />}>
            <GPSFormAITeaser
              key={state.selectedQuestionId}
              respondents={textRespondents}
              project={projectLabel}
              currentUser={currentUser}
            />
          </ErrorBoundary>
        )}

        <ErrorBoundary resetKeys={[questionSig, state.answers]} fallback={<SectionFallback />}>
          <GPSFormDetailTable
            answers={state.answers}
            loading={state.answersLoading && !state.answers}
            projectKey={projectKey}
            startDate={fmt(gps?.startDate)}
            endDate={fmt(gps?.endDate)}
            selectedQuestionLabel={selectedQuestionLabel}
            questionType={state.breakdown?.answerType ?? null}
            onClearQuestionFilter={handleClearQuestion}
            onOpenForm={handleOpenForm}
          />
        </ErrorBoundary>

        <GPSFormDetailModal
          isOpen={!!state.selectedForm}
          onClose={handleCloseForm}
          form={state.selectedForm}
          answers={safeArray(state.formDetail?.rows)}
          loading={state.formDetailLoading}
        />
      </div>
    </ErrorBoundary>
  );
};

export default GPSFormsTab;
